import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import { FormProvider } from 'react-hook-form';
import { usePaymentSubmit } from './helpers/usePaymentSubmit';
import { PaymentLayout } from './PaymentLayout';
import { usePaymentForm } from './helpers/usePaymentForm';
import { usePrefetchEffect } from '../../../../shared/router/usePrefetchEffect';
import { useNewBookingStore } from '../../../domain/booking/stores/useNewBookingsStore';
import { getTotalNewBookingPrice } from '../../../domain/booking/models/helpers/getTotalNewBookingPrice';
import { getShouldProceedWithoutPay } from '../../../domain/booking/models/helpers/getShouldProceedWithoutPay';
import { getOriginalGrandTotalPrice } from '../../../domain/booking/models/helpers/getOriginalGrandTotalPrice';
import { useSelectCardFormDefaultValues } from './helpers/useSelectCardFormDefaultValues';

interface IPaymentFormProps {
	nextUrl: string;
	sessionExpiredNextUrl: string;
	changeButtonUrl?: string;
}

export const PaymentForm = observer(
	({ nextUrl, sessionExpiredNextUrl, changeButtonUrl }: IPaymentFormProps) => {
		const { submit, paymentError, proceedWithoutPay } =
			usePaymentSubmit(nextUrl);
		const { originalBooking, booking } = useNewBookingStore();

		usePrefetchEffect(nextUrl);

		const form = usePaymentForm(useSelectCardFormDefaultValues());
		const originalTotalPrice =
			originalBooking && getOriginalGrandTotalPrice(originalBooking);
		const totalPrice = getTotalNewBookingPrice(booking);
		const shouldProceedWithoutPay = getShouldProceedWithoutPay(
			originalTotalPrice,
			totalPrice,
		);

		const submitForm = useCallback(
			(event: React.FormEvent<HTMLFormElement>) => {
				if (shouldProceedWithoutPay) {
					event.preventDefault();
					proceedWithoutPay();
					return;
				}

				form.handleSubmit(submit)(event);
			},
			[form, proceedWithoutPay, shouldProceedWithoutPay, submit],
		);

		return (
			<form onSubmit={submitForm}>
				<FormProvider {...form}>
					<PaymentLayout
						paymentError={paymentError}
						sessionExpiredNextUrl={sessionExpiredNextUrl}
						changeButtonUrl={changeButtonUrl}
					/>
				</FormProvider>
			</form>
		);
	},
);
